import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { useUserStore } from '@/config/store/userStore'
import { afterReceiving, beforeSending } from '@/services/leaveType/hooks'

let userStore
const getConnectedUser = () => {
  if (!userStore)
    userStore = useUserStore()
  return userStore
}

const resource = 'leaveTypes'

const getAll = (axios: IFactoryFunctionRequest) => (): IFactoryResponse<Array<any>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    afterReceivingHook: afterReceiving,
  })
}

const filter = (axios: IFactoryFunctionRequest) => (query): IFactoryResponse<Array<any>> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/paginate`,
    query,
    afterReceivingHook: afterReceiving,
  })
}

const create = (axios: IFactoryFunctionRequest) => (data): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const update = (axios: IFactoryFunctionRequest) => (id, { data }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const deleteOne = (axios: IFactoryFunctionRequest) => (id): IFactoryResponse<any> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const getOne = (axios: IFactoryFunctionRequest) => (id): IFactoryResponse<any> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const addBalance = (axios: IFactoryFunctionRequest) => (id, { data }): IFactoryResponse<any> => {
return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/balance`,
    data,
    afterReceivingHook: afterReceiving,
  })
}

const deleteBalances = (axios: IFactoryFunctionRequest) => ({ data }): IFactoryResponse<any> => {
return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/leaveTypes/balance`,
    data,
    afterReceivingHook: afterReceiving,
  })
}

// new endpoint to send balances for multiple leave types in one request
const addBalances = (axios: IFactoryFunctionRequest) => ({ data }): IFactoryResponse<any> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/leaveTypes/balances`,
    data
  })
}

const countValidatorByResource = (axios: IFactoryFunctionRequest) => (resourceId: string): IFactoryResponse<{ count: number }> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/countValidator/resource/${resourceId}`,
  })
}

const replaceValidatorByResource = (axios: IFactoryFunctionRequest) => (resourceId: string, replacementResourceIds: string[] = []): IFactoryResponse<{ modifiedCount: number }> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/replaceValidator/resource/${resourceId}`,
    data: { replacementResourceIds },
  })
}

export default (axios: AxiosInstance) => ({
  getAll: getAll(useAxios(axios)),
  filter: filter(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  deleteOne: deleteOne(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  addBalance: addBalance(useAxios(axios)),
  deleteBalances: deleteBalances(useAxios(axios)),
  addBalances: addBalances(useAxios(axios)),
  countValidatorByResource: countValidatorByResource(useAxios(axios)),
  replaceValidatorByResource: replaceValidatorByResource(useAxios(axios)),
})
